import { LOCALES } from "@/libraries/web-framework/src/localization/localization.constants";

/**
 * Extracts the locale prefix from a URL pathname if present
 * Returns undefined for default locale (en) or if no locale prefix exists
 */
function getLocaleFromPath(pathname: string): string | undefined {
    const pathParts = pathname.split("/").filter(Boolean);
    const firstPart = pathParts[0];
    if (firstPart && LOCALES.includes(firstPart)) {
        return firstPart;
    }
    return undefined;
}

export function getQueryParam(param: string, search: string): string[] {
    const urlParams = new URLSearchParams(search);
    const value = urlParams.get(param);
    return value ? value.split(";") : [];
}

export function getQueryParamFromWindow(param: string) {
    return getQueryParam(param, window.location.search);
}

export function setQueryParam(key: string, values: string[]): void {
    if (typeof window === "undefined") {
        return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const value = values.join(";");

    if (value) {
        urlParams.set(key, value);
    } else {
        urlParams.delete(key);
    }

    // Build query string manually to avoid URL-encoding semicolons
    const queryString = Array.from(urlParams.entries())
        .map(([k, v]) => {
            const encodedKey = encodeURIComponent(k);
            // Keep semicolons unencoded in values
            const encodedValue = v
                .split(";")
                .map((part) => encodeURIComponent(part))
                .join(";");
            return `${encodedKey}=${encodedValue}`;
        })
        .join("&");

    const newUrl = `${window.location.pathname}${
        queryString ? `?${queryString}` : ""
    }${window.location.hash}`;
    window.history.pushState({}, "", newUrl);
}

export function setQueryParams(params: Record<string, string[]>) {
    Object.entries(params).forEach(([key, values]) => {
        setQueryParam(key, values);
    });
}

export function setSlug(value: string, opts: SlugOptions = {}): void {
    if (typeof window === "undefined") {
        return;
    }

    const { basePath } = opts;

    // Extract locale from current URL path
    const currentLocale = getLocaleFromPath(window.location.pathname);

    let newPath: string;

    if (basePath) {
        // Treat basePath as non-changeable
        const basePathParts = basePath.split("/").filter(Boolean);

        // If a part exists after the base path, remove it (it will be replaced by the new slug)
        // If no part exists after the base path, remove nothing (just append the new slug)
        // Result is always: [locale] + basePath + new slug
        if (currentLocale) {
            newPath = `/${currentLocale}/${basePathParts.join("/")}/${value}`;
        } else {
            newPath = `/${basePathParts.join("/")}/${value}`;
        }
    } else {
        // Original behavior when basePath is not specified
        const pathParts = window.location.pathname.split("/").filter(Boolean);
        newPath =
            pathParts.length > 0
                ? `/${pathParts.join("/")}/${value}`
                : `/${value}`;
    }

    const newUrl = `${newPath}${window.location.search}${window.location.hash}`;
    window.history.pushState({}, "", newUrl);
}

export interface SlugOptions {
    basePath?: string;
}
export function getSlug(
    pathname: string,
    opts: SlugOptions = {}
): string | undefined {
    const { basePath } = opts;

    const currentPathParts = pathname.split("/").filter(Boolean);

    if (basePath) {
        const basePathParts = basePath.split("/").filter(Boolean);

        // Check if current path starts with base path
        const startsWithBase = basePathParts.every(
            (part, index) => currentPathParts[index] === part
        );

        if (startsWithBase && currentPathParts.length > basePathParts.length) {
            // Return the part after the basePath
            return currentPathParts[basePathParts.length];
        }

        // No part exists after the basePath, return undefined
        return undefined;
    } else {
        // No basePath specified, return the last path part
        return currentPathParts.length > 0
            ? currentPathParts[currentPathParts.length - 1]
            : undefined;
    }
}

export function getSlugFromWindow(opts: SlugOptions = {}) {
    return getSlug(window.location.pathname, opts);
}

export function clearSlug(opts: SlugOptions = {}): void {
    if (typeof window === "undefined") {
        return;
    }

    const { basePath } = opts;

    // Extract locale from current URL path
    const currentLocale = getLocaleFromPath(window.location.pathname);

    const currentPathParts = window.location.pathname
        .split("/")
        .filter(Boolean);

    if (basePath) {
        const basePathParts = basePath.split("/").filter(Boolean);

        // Check if current path starts with base path
        const startsWithBase = basePathParts.every(
            (part, index) => currentPathParts[index] === part
        );

        // Only clear if there's a part after the base path
        if (startsWithBase && currentPathParts.length > basePathParts.length) {
            // Remove the part after the basePath, preserving locale
            let newPath: string;
            if (currentLocale) {
                newPath = `/${currentLocale}/${basePathParts.join("/")}`;
            } else {
                newPath = `/${basePathParts.join("/")}`;
            }
            const newUrl = `${newPath}${window.location.search}${window.location.hash}`;
            window.history.pushState({}, "", newUrl);
        }
        // If no part exists after the base path, don't change the URL
    } else {
        // No basePath specified, use original behavior
        const pathParts = [...currentPathParts];
        if (pathParts.length > 0) {
            pathParts.pop();
        }

        const newPath = pathParts.length > 0 ? `/${pathParts.join("/")}` : "/";

        const newUrl = `${newPath}${window.location.search}${window.location.hash}`;
        window.history.pushState({}, "", newUrl);
    }
}
